import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import HamburgerMenu from 'react-hamburger-menu';
import { useAuth } from '../../AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { currentUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = (path) => (e) => {
    // If we're already on this page
    if (location.pathname === path) {
      e.preventDefault();
      // Close the menu if it's open (mobile view)
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const menuItems = [
    {
      label: 'Solutions',
      items: [
        { title: 'Field Service', description: 'Manage your service operations', link: '/field-service' },
        { title: 'HVAC', description: 'Complete HVAC business solutions', link: '/hvac' },
        { title: 'Plumbing', description: 'Streamline plumbing operations', link: '/plumbing' },
        { title: 'Electrical', description: 'Electrical service management', link: '/electrical' },
        { title: 'Roofing', description: 'Roofing service management', link: '/roofing' },
        { title: 'Construction', description: 'Construction service management', link: '/construction' },
        { title: 'Demolition', description: 'Demolition service management', link: '/demolition' },
        { title: 'General Contracting', description: 'General contracting service management', link: '/general' }
      ]
    },
    {
      label: 'Features',
      items: [
        { title: 'Invoicing', description: 'Get paid faster with digital invoicing', link: '/features/invoices' },
        { title: 'Payments', description: 'Accept payments online', link: '/features/payments' },
        { title: 'Scheduling', description: 'Optimize technician routes and bookings', link: '/features/scheduling' },
        { title: 'Lead Management', description: 'Capture and convert more leads', link: '/features/leads' },
        { title: 'Customer Management', description: 'Track customer information and history', link: '/features/customers' },
        { title: 'Pricebook', description: 'Standardize pricing across your business', link: '/features/pricebook' },
        { title: 'Estimates', description: 'Create professional digital estimates', link: '/features/estimates' },
        { title: 'Service Agreements', description: 'Build predictable recurring revenue', link: '/features/serviceagreement' },
        { title: 'Diagnostics', description: 'Advanced troubleshooting tools', link: '/features/diagnostics' },
        { title: 'Inspections', description: 'Create thorough digital inspections', link: '/features/inspections' },
        { title: 'Manual J Calculations', description: 'Accurate HVAC load calculations', link: '/features/manualJ' },
        { title: 'Plumbing Calculations', description: 'Precise pipe sizing and flow rates', link: '/features/plumbingCalc' },
        { title: 'QuickBooks Integration', description: 'Streamline accounting workflows', link: '/features/quickbooks' },
        { title: 'Reviews', description: 'Build your online reputation', link: '/features/reviews' },
        { title: 'All Features', description: 'Browse all available features', link: '/features' }
      ]
    },
    {
      label: 'Websites',
      items: [
        { title: 'Websites', description: 'Create and manage websites', link: '/features/website?demo=website' },
        { title: 'Chatbots', description: 'Create and manage chatbots', link: '/features/website?demo=chatbot' },
        { title: 'SEO', description: 'Search engine optimization', link: '/features/website?demo=seo' },
        { title: 'Social Media', description: 'Social media management', link: '/features/website?demo=socialmedia' },
        { title: 'Funnel', description: 'Create and manage funnels', link: '/features/website?demo=funnel' },
        { title: 'Business Integration', description: 'Integrate with other business platforms', link: '/features/website?demo=integration' }
      ]
    },
    { label: 'Blog', link: '/blog' },
    { label: 'Pricing', link: '/features' },
    { label: 'About', link: '/about' }
  ];

  // Add auth buttons/links to the menu items if user is not logged in
  if (!currentUser) {
    menuItems.push(
      { label: 'Sign In', link: '/login', isButton: true, buttonStyle: 'outline' },
      { label: 'Sign Up', link: '/register', isButton: true, buttonStyle: 'solid' }
    );
  } else {
    menuItems.push(
      { label: 'Account', link: '/landing', isButton: true, buttonStyle: 'outline' }
    );
  }

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 h-16 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-md' 
          : 'bg-transparent'
      } ${
        isMenuOpen ? 'shadow-md bg-white' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
        <Link 
          to="/" 
          className="text-2xl font-bold text-blue-600"
          onClick={handleNavClick("/")}
          tabIndex="0"
          aria-label="Go to homepage"
        >
          Fixetra
        </Link>

        <nav className={`${isMenuOpen ? 'block' : 'hidden'} md:block ${
          isMenuOpen 
            ? 'fixed top-16 left-0 right-0 bg-white shadow-md overflow-y-auto max-h-[calc(100vh-4rem)] z-40 p-4 border-t border-gray-200' 
            : ''
        }`}>
          <div className={`${isMenuOpen ? 'flex flex-col space-y-4' : 'hidden md:flex md:items-center md:space-x-8'}`}>
            {menuItems.map((item, index) => (
              <div 
                key={index} 
                className={`relative group ${isMenuOpen ? '' : 'h-16 flex items-center'}`}
                data-feature-dropdown={item.label === 'Features' ? "true" : "false"}
              >
                {item.items ? (
                  <>
                    <span className={`${
                      isScrolled || isMenuOpen ? 'text-gray-900' : 'text-white'
                    } cursor-pointer font-medium hover:text-blue-500 transition-colors ${
                      isMenuOpen ? 'py-3' : ''
                    }`}>
                      {item.label}
                    </span>
                    <div className={`${
                      isMenuOpen 
                        ? 'static mt-2 p-0 shadow-none w-full' 
                        : 'absolute top-full left-0 mt-0 p-4 bg-white rounded-md shadow-lg invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 z-50 w-64'
                    } ${
                      item.label === 'Features' || item.label === 'Solutions' 
                        ? 'max-h-[70vh] overflow-y-auto grid grid-cols-1 gap-1 md:w-72 md:pr-4' 
                        : ''
                    }`}>
                      {item.items.map((subItem, subIndex) => (
                        <Link 
                          key={subIndex} 
                          to={subItem.link} 
                          className="p-3 rounded hover:bg-gray-100 block transition-colors"
                          onClick={handleNavClick(subItem.link)}
                          tabIndex="0"
                        >
                          <h4 className="font-medium text-gray-900">{subItem.title}</h4>
                          <p className="text-sm text-gray-600">{subItem.description}</p>
                        </Link>
                      ))}
                    </div>
                  </>
                ) : item.isButton ? (
                  item.buttonStyle === 'outline' ? (
                    <Link 
                      to={item.link} 
                      className={`${
                        isMenuOpen 
                          ? 'w-full justify-center py-3 px-2 block' 
                          : 'min-w-[90px] text-center whitespace-nowrap'
                      } border border-blue-500 text-blue-500 px-4 py-2 rounded-md hover:bg-blue-50 transition-colors`}
                      onClick={handleNavClick(item.link)}
                      tabIndex="0"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Link 
                      to={item.link} 
                      className={`${
                        isMenuOpen 
                          ? 'w-full justify-center py-3 px-2 block' 
                          : 'min-w-[90px] text-center whitespace-nowrap'
                      } bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors`}
                      onClick={handleNavClick(item.link)}
                      tabIndex="0"
                    >
                      {item.label}
                    </Link>
                  )
                ) : (
                  <Link 
                    to={item.link} 
                    className={`${
                      isScrolled || isMenuOpen ? 'text-gray-900' : 'text-white'
                    } font-medium hover:text-blue-500 transition-colors ${
                      isMenuOpen ? 'py-3 block w-full' : ''
                    }`}
                    onClick={handleNavClick(item.link)}
                    tabIndex="0"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </nav>

        <div className="block md:hidden">
          <HamburgerMenu
            isOpen={isMenuOpen}
            menuClicked={toggleMenu}
            width={18}
            height={15}
            strokeWidth={2}
            rotate={0}
            color={isScrolled || isMenuOpen ? 'black' : 'white'}
            borderRadius={0}
            animationDuration={0.5}
          />
        </div>
      </div>
    </header>
  );
};

export default Header; 